/**
 * Socket.IO Client Initialization
 * Connects to backend socket.io server and integrates with hybridSocket
 */

import { io } from 'socket.io-client';
import axios from 'axios';
import hybridSocket from '../offline/hybridSocket';

class SocketClient {
  constructor() {
    this.socket = null;
    this.isInitialized = false;
  }

  /**
   * Initialize socket.io connection
   */
  initialize() {
    if (this.isInitialized) {
      console.log('⚠️ Socket.IO already initialized');
      return this.socket;
    }

    // Use axios baseURL (set in UrlAndPaths.js) for socket connection
    const backendUrl = axios.defaults.baseURL?.replace(/\/$/, '') || '';
    
    // If browser is offline, don't attempt connection - hybridSocket handles offline mode
    if (!navigator.onLine) {
      console.log('📴 Browser is offline, skipping socket connection. hybridSocket will handle offline mode.');
      hybridSocket.setBackendStatus(false);
      this.isInitialized = true;
      return null;
    }
    
    console.log('🔌 Connecting to Socket.IO server:', backendUrl);
    
    this.socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000
    });
    
    console.log('🔌 Socket.IO instance created');

    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ Socket.IO connected:', this.socket.id);
      hybridSocket.setBackendStatus(true);
      hybridSocket.setRealSocket(this.socket);
      
      // Authenticate with branch and user info from JWT token
      const authenticate = () => {
        try {
          const token = localStorage.getItem('userToken');
          if (!token) {
            console.error('❌ No userToken found in localStorage');
            return;
          }

          // Decode JWT to get user info
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          
          const decoded = JSON.parse(jsonPayload);
          const userId = decoded._id;
          const branchId = decoded.branchId;
          
          if (userId && branchId) {
            this.socket.emit('setUsername', userId, branchId);
            console.log('📡 Socket.IO authenticated:', { userId, branchId });
          } else {
            console.error('❌ userId or branchId not found in JWT token');
          }
        } catch (error) {
          console.error('❌ Error decoding JWT token:', error);
        }
      };
      
      authenticate();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket.IO disconnected:', reason);
      hybridSocket.setBackendStatus(false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket.IO connection error:', error.message);
      hybridSocket.setBackendStatus(false);
    });

    this.socket.on('ready', (data) => {
      console.log('✅ Socket.IO ready:', data);
    });

    this.socket.on('error', (error) => {
      console.error('❌ Socket.IO error:', error);
    });

    this.isInitialized = true;
    return this.socket;
  }

  /**
   * Disconnect socket
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isInitialized = false;
      console.log('🔌 Socket.IO disconnected');
    }
  }

  /**
   * Get socket instance
   */
  getSocket() {
    return this.socket;
  }
}

// Export singleton instance
const socketClient = new SocketClient();
export default socketClient;
